import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './contacts.module.scss';
import { Spinner, Button } from 'react-bootstrap';
import { useLocation } from 'react-router';
import Pagination from 'v2/common/Pagination/Pagination';
import ContactsList from 'v2/common/ContactsList/ContactsList';
import CreateContactModal from 'v2/common/ContactModals/CreateContactModal/CreateContactModal';
import SearchBar from 'v2/common/SearchBar/SearchBar';
import EmptyPlaceholder from 'v2/common/DRM/ListPages/EmptyPlaceholder';
import BulkImport from 'v2/common/BulkImport/BulkImport';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';
import { useFeatureFlags } from 'v2/hooks/useFeatureFlags';
import { FEATURE_FLAGS } from 'utils/constants';
import { useAdvancedFilters } from 'v2/hooks/useAdvancedFilters';
import { ContactFilterableFields } from 'v2/common/ComposeFilters/filterableFields';
import { useContacts } from 'v2/queries/useContacts';
import { FiltersBar } from 'v2/common/ComposeFilters/FiltersBar';

const Contacts = () => {
  const { t } = useTranslation();
  const [showCreateContactModal, setShowCreateContactModal] = useState(false);
  const location = useLocation();

  const [isAdvancedFiltersEnabled] = useFeatureFlags([
    FEATURE_FLAGS.advanced_filters,
  ]);

  const filterableFields = ContactFilterableFields;
  const {
    handleChangeFilters,
    handleChangeSearch,
    handleChangePage,
    page,
    searchText,
    filters,
  } = useAdvancedFilters('/contacts', filterableFields);

  const {
    data: { results: contactsData, count: contactsCount = 0 } = {},
    refetch,
    isFetching,
  } = useContacts({ page, searchText, filters });

  useEffect(() => {
    refetch();
  }, [location.pathname, location.search]);

  function changePage(page: number) {
    handleChangePage(page);
    window.scrollTo(0, 0);
  }

  const handleCloseContactModal = () => {
    setShowCreateContactModal(false);
    refetch();
  };

  return (
    <div className={classnames('container-xl', styles.listContainer)}>
      <div
        className={classnames(
          'd-flex justify-content-between position-relative z-index-1',
          styles.titleBackground
        )}
        style={{ height: '122px' }}
      >
        <div className="container-xl d-flex justify-content-between position-relative fix-bg-images px-0">
          <div className="d-flex align-items-center text-truncate">
            <h1 className="m-0 text-truncate pr-1" role="heading">
              {t(TranslationKeys.contact_one, {
                postProcess: PostProcessKeys.TitleCase,
              })}
            </h1>
          </div>
          <div className={styles.actionButtonsContainer}>
            <BulkImport />
            <Button
              type="button"
              className={classnames(styles.addButton)}
              variant="secondary"
              size="sm"
              onClick={() => setShowCreateContactModal(true)}
              data-cy="contacts-list:create-contact-button"
            >
              Add new {t(TranslationKeys.contact_one)}
            </Button>
            <CreateContactModal
              show={showCreateContactModal}
              onClose={handleCloseContactModal}
            />
          </div>
        </div>
      </div>
      <div className={classnames('container-xl', styles.filterRow)}>
        <div className={classnames(styles.filterContainer)}>
          <div className={classnames('d-flex align-items-center row p-0')}>
            {isAdvancedFiltersEnabled && (
              <FiltersBar
                filterableFields={filterableFields}
                filters={filters}
                searchText={searchText}
                onFiltersChange={handleChangeFilters}
                onSearchChange={handleChangeSearch}
              />
            )}
            {!isAdvancedFiltersEnabled && (
              <SearchBar
                placeHolder="Search"
                currentText={searchText || ''}
                onSearch={handleChangeSearch}
                lengthTextValidation={{
                  operator: '>',
                  number: -1,
                  debounce: 500,
                }}
              />
            )}
          </div>
        </div>
      </div>

      {isFetching ? (
        <div className="loadingIndicatorContainer" aria-label="loading-spinner">
          <Spinner animation="border" />
        </div>
      ) : !searchText || contactsCount ? (
        <>
          <ContactsList list={contactsData} />
          {contactsCount > 10 && (
            <Pagination
              totalCountOfItems={contactsCount}
              callBack={changePage}
              currentPageOfItems={page}
            />
          )}
        </>
      ) : (
        <EmptyPlaceholder message="Try adjusting your search or filter to find what you’re looking for." />
      )}
    </div>
  );
};

export default Contacts;
